import { useCallback, useMemo, useState } from "react";
import type { CanvasState as OrchestratorCanvasState } from "../services/orchestrator.js";
import { orchestratorAgent } from "../services/orchestrator.js";
import { useCanvasStore } from "../stores/canvasStore.js";
import type { CanvasContext, UIMessage } from "../types/messages.js";

type UseAgentOptions = {
  body?: { canvasState?: CanvasContext };
  enableVisualFeedback?: boolean;
  streaming?: boolean;
};

/**
 * useOrchestratorAgent
 *
 * Formerly useMockAgent – now the default live agent hook that
 * calls the in-app orchestrator and executes real tool calls.
 */
export function useOrchestratorAgent(options?: UseAgentOptions) {
  const [messages, setMessages] = useState<UIMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [status, setStatus] = useState<"idle" | "generating" | "error">("idle");
  const [input, setInput] = useState("");

  const { activeCanvasId, canvases } = useCanvasStore();
  const activeCanvas = activeCanvasId
    ? canvases.find((c) => c.id === activeCanvasId)
    : null;

  // Build canvas state from override (if provided) or from store
  const derivedCanvasState = useMemo<CanvasContext | null>(() => {
    if (options?.body?.canvasState) return options.body.canvasState;
    if (!activeCanvasId || !activeCanvas) return null;
    return {
      canvasId: activeCanvasId,
      widgets: (activeCanvas.widgets || []).map((w: any) => ({
        id: w.id,
        type: w.type,
        title: w.config?.title,
        position: w.position,
        outputs: w.outputs,
      })),
      connections: activeCanvas.connections || [],
    };
  }, [options?.body?.canvasState, activeCanvasId, activeCanvas]);

  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim() || !activeCanvasId) return;

      // Record the user prompt locally for chat history, but do NOT
      // block the UI or disable input. Runs should be fire-and-forget.
      const userMessage: UIMessage = {
        id: Date.now().toString(),
        role: "user",
        content: content,
        parts: [{ type: "text", text: content }],
      };
      setMessages((prev) => [...prev, userMessage]);
      setError(null);
      setStatus("idle");
      setIsLoading(false);

      try {
        // Build current canvas state; context for each run is the canvas only
        const canvasStateRaw = derivedCanvasState || {
          canvasId: activeCanvasId,
          widgets: activeCanvas?.widgets || [],
          connections: activeCanvas?.connections || [],
        };
        const canvasState: OrchestratorCanvasState = {
          ...canvasStateRaw,
          widgets: (canvasStateRaw as any).widgets.map((w: any) => ({
            id: w.id,
            type: w.type,
            title: w.title ?? w.config?.title,
            position: w.position,
            outputs: w.outputs ?? {},
          })),
          connections: (canvasStateRaw as any).connections || [],
        };

        // IMPORTANT: Each prompt starts a new run with no prior chat context.
        const singleTurnMessages = [
          { role: "user" as const, content },
        ];

        // Fire-and-forget; do not await. The canvas will update via tool calls.
        orchestratorAgent
          .executeTask(singleTurnMessages, { canvasState })
          .catch((err) => {
            const errorObj = err instanceof Error ? err : new Error("Unknown error");
            setError(errorObj);
            // Append a lightweight error note to the chat transcript for visibility
            setMessages((prev) => [
              ...prev,
              {
                id: (Date.now() + 1).toString(),
                role: "assistant",
                content: `Error starting run: ${errorObj.message}`,
                parts: [{ type: "text", text: `Error starting run: ${errorObj.message}` }],
              },
            ]);
          });
      } catch (err) {
        const errorObj = err instanceof Error ? err : new Error("Unknown error");
        setError(errorObj);
        setMessages((prev) => [
          ...prev,
          {
            id: (Date.now() + 1).toString(),
            role: "assistant",
            content: `Error: ${errorObj.message}`,
            parts: [{ type: "text", text: `Error: ${errorObj.message}` }],
          },
        ]);
      }
    },
    [activeCanvasId, activeCanvas, derivedCanvasState]
  );

  const regenerate = useCallback(async () => {
    if (messages.length < 2) return;

    // Remove last assistant message and resend
    const lastUserMessage = [...messages]
      .reverse()
      .find((m) => m.role === "user");
    if (lastUserMessage) {
      const content =
        lastUserMessage.parts?.find((p) => p.type === "text")?.text ||
        lastUserMessage.content;
      if (content) {
        setMessages(messages.slice(0, -1));
        await sendMessage(content);
      }
    }
  }, [messages, sendMessage]);

  const stop = useCallback(() => {
    setIsLoading(false);
    setStatus("idle");
  }, []);

  const clearError = useCallback(() => {
    setError(null);
    setStatus("idle");
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setInput(e.target.value);
    },
    []
  );

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();
      if (input.trim()) {
        await sendMessage(input);
        setInput("");
      }
    },
    [input, sendMessage]
  );

  return {
    messages,
    sendMessage,
    regenerate,
    stop,
    status,
    error,
    setMessages,
    clearError,
    id: "orchestrator-agent",
    input,
    handleInputChange,
    handleSubmit,
    isLoading,
    canRegenerate: messages.length > 1,
  };
}

export type OrchestratorAgentHelpers = ReturnType<typeof useOrchestratorAgent>;
